import { LogParser, data_t } from 'foreman-slink';
import { IntlShape } from 'react-intl';
import { convertUnitNumber, unitTypeString } from '../helpers/chartHelpers';
import { JobLogV2Data } from './types';
import { EmailGuestShare, JobLogPermission } from '../../models/jobLog';

/** A function that converts the output of the slink log parser to a fully-serializable form. */
export const logParserDataToJobLogV2 = (data: data_t): JobLogV2Data => {
  // Replaced deep clone with structured clone to avoid removal of negative indexes in arrays
  return {
    ...structuredClone(data),
    timeTable: data.timeTable.map(i => ({ msElapsed: i.msElapsed, calendar: i.calendar.toISOString() })),
  };
};

/** Internal use only. The initial state of a parsed job log. */
let defaultLogParserData: data_t;

/** Returns the initial structure of a parsed job log. */
export const defaultLogData = (): JobLogV2Data => {
  if (!defaultLogParserData) defaultLogParserData = new LogParser().data;
  return logParserDataToJobLogV2(defaultLogParserData);
};

interface LogTime {
  msElapsed: number;
  calendar: number;
}

/**
 * A utility that takes a job log's `timeTable` and provides the ability to map between
 * relative `msElapsed` values and absolute instant values (in epoch ms).
 *
 * Use {@link TimeTable.from} for constructing new instances.
 */
export class TimeTable {
  /** Constructs a new {@link TimeTable}, returning a cached version if available. */
  public static readonly from = (logTime: JobLogV2Data['timeTable']): TimeTable => {
    const existing = TimeTable.CACHE.get(logTime);
    if (existing) return existing;

    const result = new TimeTable(logTime);
    TimeTable.CACHE.set(logTime, result);
    return result;
  };

  /** Given an `msElapsed` value, returns the key to use in the lookup table. */
  private static readonly getLookupKey = (msElapsed: number): number => {
    return Math.floor(msElapsed / TimeTable.BLOCK_SIZE);
  };

  private static readonly CACHE = new WeakMap<JobLogV2Data['timeTable'], TimeTable>();
  private static readonly BLOCK_SIZE = 1000 * 60 * 3;

  /**
   * A map where the key is a value returned by {@link TimeTable.getLookupKey} (based on an msElapsed value),
   * and the value is an index into {@link TimeTable.logTime} where we should start scanning.
   */
  private readonly lookup = new Map<number, number>();
  /** The greatest key in {@link TimeTable.lookup}. */
  private readonly lastKey: number;
  /** The actual data backing the time table, formatted for fastest use. */
  private readonly logTime: LogTime[];

  /** @deprecated Do not invoke the constructor directly. Use {@link TimeTable.from}. */
  constructor(tt: JobLogV2Data['timeTable']) {
    const logTime: LogTime[] = tt.map(i => ({ msElapsed: i.msElapsed, calendar: new Date(i.calendar).getTime() }));
    if (logTime.length === 0) throw new Error('Cannot construct a TimeTable with 0 reference elements!');
    this.logTime = logTime;

    this.lastKey = TimeTable.getLookupKey(logTime[logTime.length - 1].msElapsed);

    for (let i = logTime.length - 1; i >= 0; i--) {
      const time = logTime[i];
      const key = TimeTable.getLookupKey(time.msElapsed);
      if (this.lookup.has(key)) continue;
      this.lookup.set(key, i);
    }

    for (let k = this.lastKey; k >= 0; k--) {
      if (this.lookup.has(k)) continue;
      const closest = this.lookup.get(k + 1);
      if (closest === undefined)
        throw new Error('Could not get closest neighbor for lookup! This should never be hit.');
      this.lookup.set(k, closest);
    }
  }

  /**
   * For the provided relative `msElapsed` value, returns an instant expressed in epoch milliseconds that this event occurred.
   */
  public readonly getInstant = (msElapsed: number): number => {
    const key = TimeTable.getLookupKey(msElapsed);
    const startingIndex = this.lookup.get(key) ?? this.logTime.length - 1; // It's possible that `msElapsed` is greater than any number in the time table. If so, we want to start scanning from the end.

    for (let i = startingIndex; i >= 0; i--) {
      const time = this.logTime[i];
      if (time.msElapsed <= msElapsed) {
        return time.calendar + msElapsed - time.msElapsed;
      }
    }

    const time = this.logTime[0];
    return time.calendar + msElapsed - time.msElapsed;
  };

  /**
   * For the provided absolute `calendar` value (expressed in epoch ms), returns the relative ms elapsed value that this event occurred.
   */
  public readonly getMsElapsed = (calendar: number): number => {
    for (let i = this.logTime.length - 1; i >= 0; i--) {
      const time = this.logTime[i];
      if (calendar >= time.calendar) {
        const next = this.logTime[i + 1];
        const diff = calendar - time.calendar;
        return Math.min(time.msElapsed + diff, next?.msElapsed ?? Number.MAX_SAFE_INTEGER);
      }
    }

    const time = this.logTime[0];
    const diff = calendar - time.calendar;
    return time.msElapsed + diff;
  };
}

/**
 * Function converts milliseconds to a string
 * @param duration The number of ms
 * @returns String in format "X minutes, Y seconds"
 */
export const msToTime = (duration: number, formatNumber: IntlShape['formatNumber']) => {
  let tempDuration = duration;
  const days = Math.floor(tempDuration / (1000 * 60 * 60 * 24));
  tempDuration -= days * (1000 * 60 * 60 * 24);
  const hours = Math.floor(tempDuration / (1000 * 60 * 60));
  tempDuration -= hours * (1000 * 60 * 60);
  const minutes = Math.floor(tempDuration / (1000 * 60));
  tempDuration -= minutes * (1000 * 60);
  const seconds = tempDuration / 1000;

  const result: string[] = [];

  if (days) {
    result.push(formatNumber(days, { style: 'unit', unit: 'day', unitDisplay: 'long' }));
  }
  if (hours) {
    result.push(formatNumber(hours, { style: 'unit', unit: 'hour', unitDisplay: 'long' }));
  }
  if (minutes) {
    result.push(formatNumber(minutes, { style: 'unit', unit: 'minute', unitDisplay: 'long' }));
  }
  const showPreciseSeconds = result.length === 0;
  if (Math.floor(seconds) || showPreciseSeconds) {
    const maximumFractionDigits = showPreciseSeconds ? 2 : 0;
    const value = showPreciseSeconds ? seconds : Math.floor(seconds);
    result.push(
      formatNumber(value, {
        style: 'unit',
        unit: 'second',
        unitDisplay: 'long',
        maximumFractionDigits,
      }),
    );
  }

  return result.join(', ');
};

/**
 * Calculates the average lance speed given a log's tubesheet data.
 * @param tubeSheets The tube sheet data of the job log.
 * @param isMetric Flag indicating if result should be in metric measurements.
 * @param timeRangeMin Minimum time used to deterimine which lance speed events to include in the average.
 * @param timeRangeMax Maximum time used to deterimine which lance speed events to include in the average.
 * @returns A formatted string of the average lance speed, including a label.
 */
export const calcLanceSpeed = (
  tubeSheets: JobLogV2Data['tubeSheet'],
  isMetric: boolean,
  timeRangeMin: number,
  timeRangeMax: number,
): string => {
  if (!tubeSheets || tubeSheets.length === 0) {
    return 'N/A';
  }

  let avgLanceSpeeds = 0;
  let numberOfLanceSpeeds = 0;
  for (const tubeSheet of tubeSheets) {
    for (const row in tubeSheet.tubes) {
      for (const col in tubeSheet.tubes[row]) {
        const tube = tubeSheet.tubes[row][col];
        const tubeEvents = tube.eventInfo.filter(event =>
          !!timeRangeMax ? event.msElapsed >= timeRangeMin && event.msElapsed <= timeRangeMax : true,
        );

        // If there are no events within the time range
        if (!tubeEvents?.length) break;

        const avgLanceSpeed = tubeEvents.reduce((prev, item) => {
          const timeIn = item.timeIn / 1000; // ms -> seconds
          const timeOut = item.timeOut / 1000; // ms -> seconds

          if (timeIn > 0 && timeOut > 0) {
            return prev + (item.depth / timeIn + item.depth / timeOut) / 2;
          }

          if (timeIn > 0) {
            return prev + item.depth / timeIn;
          }

          if (item.timeOut > 0) {
            return prev + item.depth / timeOut;
          }

          return prev;
        }, 0);

        // Converts to cm/s or ft/s
        const convertedLanceSpeed = convertUnitNumber(avgLanceSpeed, 'lanceSpeed', isMetric);

        avgLanceSpeeds += convertedLanceSpeed;
        numberOfLanceSpeeds++;
      }
    }
  }

  const averageLanceSpeed = !numberOfLanceSpeeds ? 0 : avgLanceSpeeds / numberOfLanceSpeeds;

  return `${averageLanceSpeed.toFixed(3)} ${unitTypeString('lanceSpeed', isMetric)}`;
};

/**
 * Returns permissions minus any that are expired.
 *
 * The backend will not return a log entry where all permissions are expired (setting aside email shares),
 * but it might return a log entry that contains one or more expired shares and one or more unexpired shares.
 * We need to make sure the expired ones are filtered out to prevent them from being displayed and counted.
 *
 * @param permissions
 */
export const filterOutExpiredPermissions = (permissions: JobLogPermission[] | undefined): JobLogPermission[] => {
  const now = Date.now();
  return permissions?.filter(p => !p.expiresOn || (p.expiresOn && now < p.expiresOn)) ?? [];
};

/**
 *
 */
export const filterOutExpiredEmailGuestShares = (
  emailGuestShares: EmailGuestShare[] | undefined,
): EmailGuestShare[] => {
  const now = Date.now();
  return emailGuestShares?.filter(e => now < e.expiresOn) ?? [];
};
